/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: modules/wireguard-client/api/wireguardClientApi.ts
 🗓️ Created: 2026-05-13
 🧩 Description:
    API calls for the WireGuard Client module (Ring2All PBX).
    Mirrors openvpnClientApi.ts pattern exactly.
────────────────────────────────────────────────────────────────*/

import type { WireGuardClientStatus } from '../types';

const API_BASE = '/api/network/wireguard-client';

function getApiHeaders(tenantSlug?: string, userId?: string): HeadersInit {
    const headers: HeadersInit = {
        'Content-Type': 'application/json'
    };
    if (tenantSlug) {
        headers['X-Tenant-Slug'] = tenantSlug;
    }
    if (userId) {
        headers['X-User-Id'] = userId;
    }
    return headers;
}

export interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: string;
}

/**
 * Get WireGuard tunnel status
 */
export async function getWireGuardClientStatus(
    tenantSlug?: string,
    userId?: string
): Promise<ApiResponse<WireGuardClientStatus>> {
    const response = await fetch(`${API_BASE}/status`, {
        headers: getApiHeaders(tenantSlug, userId)
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error((error as any).error || `HTTP ${response.status}`);
    }

    return response.json();
}

/**
 * Upload / import a WireGuard .conf file
 */
export async function uploadWireGuardConfig(
    wgConfContent: string,
    tenantSlug?: string,
    userId?: string
): Promise<ApiResponse<WireGuardClientStatus>> {
    const response = await fetch(`${API_BASE}/upload`, {
        method: 'POST',
        headers: getApiHeaders(tenantSlug, userId),
        body: JSON.stringify({ wgConfContent })
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error((error as any).error || `HTTP ${response.status}`);
    }

    return response.json();
}

/**
 * Connect the WireGuard tunnel
 */
export async function connectWireGuard(
    tenantSlug?: string,
    userId?: string
): Promise<ApiResponse<WireGuardClientStatus>> {
    const response = await fetch(`${API_BASE}/connect`, {
        method: 'POST',
        headers: getApiHeaders(tenantSlug, userId),
        body: JSON.stringify({})
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error((error as any).error || `HTTP ${response.status}`);
    }

    return response.json();
}

/**
 * Disconnect the WireGuard tunnel
 */
export async function disconnectWireGuard(
    tenantSlug?: string,
    userId?: string
): Promise<ApiResponse<WireGuardClientStatus>> {
    const response = await fetch(`${API_BASE}/disconnect`, {
        method: 'POST',
        headers: getApiHeaders(tenantSlug, userId),
        body: JSON.stringify({})
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error((error as any).error || `HTTP ${response.status}`);
    }

    return response.json();
}

/**
 * Delete the WireGuard configuration file
 */
export async function deleteWireGuardConfig(
    tenantSlug?: string,
    userId?: string
): Promise<ApiResponse<{ success: boolean }>> {
    const response = await fetch(`${API_BASE}/config`, {
        method: 'DELETE',
        headers: getApiHeaders(tenantSlug, userId)
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error((error as any).error || `HTTP ${response.status}`);
    }

    return response.json();
}

/**
 * Refresh the current tunnel status
 */
export async function refreshWireGuardStatus(
    tenantSlug?: string,
    userId?: string
): Promise<ApiResponse<WireGuardClientStatus>> {
    const response = await fetch(`${API_BASE}/refresh`, {
        method: 'POST',
        headers: getApiHeaders(tenantSlug, userId),
        body: JSON.stringify({})
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error((error as any).error || `HTTP ${response.status}`);
    }

    return response.json();
}
